import { useMemo, useState } from 'react';
import { Button, Checkbox, InputNumber, Space } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import type { ProColumns } from '@ant-design/pro-components';

// ─────────────────────────────────────────────────────────────────────────────
// Arbox-Expenses-style filter dropdown — multi-select with search + select-all
// + reset/apply footer. Single source of truth so every column gets the same
// look-and-feel.
// ─────────────────────────────────────────────────────────────────────────────

interface FilterOption {
  value: string;
  label: string;
}

interface MultiSelectFilterDropdownProps {
  options: FilterOption[];
  selectedKeys: React.Key[];
  setSelectedKeys: (keys: React.Key[]) => void;
  confirm: () => void;
  clearFilters?: () => void;
}

function MultiSelectFilterDropdown({
  options,
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
}: MultiSelectFilterDropdownProps) {
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const selectedSet = useMemo(
    () => new Set(selectedKeys.map((k) => String(k))),
    [selectedKeys],
  );

  // "Select all" toggles the currently-visible options (so applying it after a
  // search only flips the filtered subset, matching the Arbox-Expenses UX).
  const visibleAllSelected =
    visible.length > 0 && visible.every((o) => selectedSet.has(o.value));

  const toggleAll = () => {
    if (visibleAllSelected) {
      const visibleSet = new Set(visible.map((o) => o.value));
      setSelectedKeys(selectedKeys.filter((k) => !visibleSet.has(String(k))));
    } else {
      const merged = new Set<string>([
        ...selectedKeys.map((k) => String(k)),
        ...visible.map((o) => o.value),
      ]);
      setSelectedKeys(Array.from(merged));
    }
  };

  const toggleOne = (value: string) => {
    if (selectedSet.has(value)) {
      setSelectedKeys(selectedKeys.filter((k) => String(k) !== value));
    } else {
      setSelectedKeys([...selectedKeys, value]);
    }
  };

  return (
    // stopPropagation so Enter / Esc inside the dropdown don't bubble up and
    // close the table's row-selection state.
    <div style={{ padding: 8, minWidth: 200 }} onKeyDown={(e) => e.stopPropagation()}>
      <div style={{ paddingBottom: 8 }}>
        {/* Plain <input> (not antd's Input) to match the Arbox-Expenses
            class-based styling — keeps the visual mass identical. */}
        <div className="w-full">
          <input
            className="w-full h-[38px] px-3 py-[7px] border rounded text-body text-main-text bg-white placeholder:text-secondary-text focus:outline-none focus:border-primary focus:shadow-focus disabled:bg-grey-bg disabled:text-secondary-text disabled:cursor-not-allowed border-border"
            placeholder="חיפוש..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <label
        className="flex items-center cursor-pointer"
        style={{ paddingBottom: 8 }}
      >
        <Checkbox
          checked={visibleAllSelected}
          indeterminate={!visibleAllSelected && visible.some((o) => selectedSet.has(o.value))}
          onChange={toggleAll}
        />
        <span
          style={{
            fontSize: 13,
            color: '#3b3b3b',
            maxWidth: 180,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            marginInlineStart: 8,
            flex: '1 1 0%',
          }}
        >
          בחר הכל
        </span>
      </label>

      <div style={{ maxHeight: 192, overflowY: 'auto' }}>
        {visible.length === 0 ? (
          <div style={{ fontSize: 13, color: '#9d9d9d', padding: '4px 0' }}>
            אין תוצאות
          </div>
        ) : (
          visible.map((option) => (
            <label
              key={option.value}
              className="flex items-center cursor-pointer"
              style={{ paddingBottom: 8 }}
            >
              <Checkbox
                checked={selectedSet.has(option.value)}
                onChange={() => toggleOne(option.value)}
              />
              <span
                style={{
                  fontSize: 13,
                  color: '#3b3b3b',
                  maxWidth: 180,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  marginInlineStart: 8,
                  flex: '1 1 0%',
                }}
                title={option.label}
              >
                {option.label}
              </span>
            </label>
          ))
        )}
      </div>

      <div className="flex items-center mt-2" style={{ justifyContent: 'space-evenly' }}>
        <button
          type="button"
          onClick={() => {
            // Clear our selection state AND the parent filter; close.
            clearFilters?.();
            setSelectedKeys([]);
            setSearch('');
            confirm();
          }}
          className="rounded border border-primary text-primary bg-white hover:bg-blue-bg transition-colors"
          style={{ width: 90, fontSize: '0.875rem', padding: '4px 0' }}
        >
          איפוס
        </button>
        <button
          type="button"
          onClick={() => confirm()}
          className="rounded bg-primary text-white hover:bg-primary-hover transition-colors"
          style={{ width: 90, fontSize: '0.875rem', padding: '4px 0' }}
        >
          אישור
        </button>
      </div>
    </div>
  );
}

/**
 * Spread into a ProColumn to add the Arbox-Expenses multi-select filter
 * dropdown. The list of options is derived from the *currently loaded* rows
 * (so it grows/shrinks with the page). For columns that have a fixed value
 * set (e.g. status, payment_type), use `multiSelectColumnFixed` instead.
 *
 * `getValue` returns the underlying value used for matching + display.
 * Null/empty values are omitted from the options list (so the filter doesn't
 * show "—" as a selectable option).
 *
 * Matching is exact-equality (after String coercion). If a column wants
 * substring matching, build a custom helper.
 */
export function multiSelectColumn<T>(
  rows: T[],
  getValue: (r: T) => string | number | null | undefined,
): Partial<ProColumns<T>> {
  const options: FilterOption[] = distinctSorted(rows, getValue).map((v) => ({
    value: v,
    label: v,
  }));
  return baseMultiSelect<T>(options, (record, value) =>
    String(getValue(record) ?? '') === value,
  );
}

/**
 * Same dropdown, but with a fixed `options` array (e.g. status labels).
 * Each option has a separate `value` (what gets stored in filter state, passed
 * to the server / used in the comparator) and `label` (what the user sees).
 * `matchValue` decides whether a record passes the filter for a given
 * selected option value.
 */
export function multiSelectColumnFixed<T>(
  options: FilterOption[],
  matchValue: (record: T, value: string) => boolean,
): Partial<ProColumns<T>> {
  return baseMultiSelect<T>(options, matchValue);
}

function baseMultiSelect<T>(
  options: FilterOption[],
  matchValue: (record: T, value: string) => boolean,
): Partial<ProColumns<T>> {
  return {
    filters: options.map((o) => ({ text: o.label, value: o.value })),
    filterDropdown: (props) => (
      <MultiSelectFilterDropdown
        options={options}
        selectedKeys={props.selectedKeys}
        setSelectedKeys={props.setSelectedKeys}
        confirm={props.confirm}
        clearFilters={props.clearFilters}
      />
    ),
    filterIcon: (filtered: boolean) => (
      <FontAwesomeIcon
        icon={faFilter}
        style={{ fontSize: 12, color: filtered ? '#1936f7' : '#e0e0e0' }}
      />
    ),
    onFilter: (value, record) => matchValue(record as T, String(value)),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Numeric range filter — min/max number inputs, same Arbox-Expenses chrome
// (איפוס / אישור buttons, funnel filter-trigger icon).
// ─────────────────────────────────────────────────────────────────────────────

interface NumericRangeFilterDropdownProps {
  selectedKeys: React.Key[];
  setSelectedKeys: (keys: React.Key[]) => void;
  confirm: () => void;
  clearFilters?: () => void;
}

function NumericRangeFilterDropdown({
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
}: NumericRangeFilterDropdownProps) {
  // Encode [min, max] as a single JSON string so antd's per-key OR-filter
  // semantics don't fire onFilter twice (once per key); we want a single
  // range check.
  const current = useMemo(() => {
    const raw = selectedKeys?.[0];
    if (raw == null) return { min: null, max: null };
    try {
      const parsed = JSON.parse(String(raw)) as {
        min?: number | null;
        max?: number | null;
      };
      return { min: parsed.min ?? null, max: parsed.max ?? null };
    } catch {
      return { min: null, max: null };
    }
  }, [selectedKeys]);

  const [min, setMin] = useState<number | null>(current.min);
  const [max, setMax] = useState<number | null>(current.max);

  const apply = () => {
    if (min == null && max == null) {
      setSelectedKeys([]);
    } else {
      setSelectedKeys([JSON.stringify({ min, max })]);
    }
    confirm();
  };

  const reset = () => {
    setMin(null);
    setMax(null);
    clearFilters?.();
    setSelectedKeys([]);
    confirm();
  };

  return (
    <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <InputNumber
          placeholder="מינימום"
          value={min}
          onChange={(v) => setMin(v as number | null)}
          onPressEnter={apply}
          style={{ width: 110 }}
        />
        <div style={{ width: 8 }} />
        <InputNumber
          placeholder="מקסימום"
          value={max}
          onChange={(v) => setMax(v as number | null)}
          onPressEnter={apply}
          style={{ width: 110 }}
        />
      </div>

      <div style={{ height: 8 }} />

      <Space size="small">
        <Button size="small" onClick={reset} style={{ width: 90 }}>
          איפוס
        </Button>
        <Button size="small" type="primary" onClick={apply} style={{ width: 90 }}>
          אישור
        </Button>
      </Space>
    </div>
  );
}

/**
 * Spread into a ProColumn to add a numeric min/max range filter dropdown.
 * Min or max can be omitted (empty input) for an open-ended range. Values
 * are inclusive on both sides.
 */
export function numericRangeColumn<T>(
  getValue: (r: T) => number | null | undefined,
): Partial<ProColumns<T>> {
  return {
    filterDropdown: (props) => (
      <NumericRangeFilterDropdown
        selectedKeys={props.selectedKeys}
        setSelectedKeys={props.setSelectedKeys}
        confirm={props.confirm}
        clearFilters={props.clearFilters}
      />
    ),
    filterIcon: (filtered: boolean) => (
      <FontAwesomeIcon
        icon={faFilter}
        style={{ fontSize: 12, color: filtered ? '#1936f7' : '#e0e0e0' }}
      />
    ),
    onFilter: (rawValue, record) => {
      let min: number | null = null;
      let max: number | null = null;
      try {
        const parsed = JSON.parse(String(rawValue)) as {
          min?: number | null;
          max?: number | null;
        };
        if (parsed.min != null) min = Number(parsed.min);
        if (parsed.max != null) max = Number(parsed.max);
      } catch {
        return true;
      }
      const v = getValue(record as T);
      if (v == null) return min == null && max == null;
      if (min != null && v < min) return false;
      if (max != null && v > max) return false;
      return true;
    },
  };
}

function distinctSorted<T>(
  rows: T[],
  getValue: (r: T) => string | number | null | undefined,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of rows) {
    const v = getValue(r);
    if (v == null || v === '') continue;
    const s = String(v);
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out.sort((a, b) => a.localeCompare(b, 'he'));
}
